import {
  params,
  string,
  shape,
  bool,
  arrayOf,
  alias,
  number,
  customType
} from '@thd-nucleus/data-sources';

export const ProductDataModel = {
  clientOnlyProduct: alias('product').params({ itemId: string().isRequired() }).shape({
    itemId: string(),
    dataSources: string(),
    bundleSpecificationDetails: params({ storeId: string(), zipCode: string() }).shape({
      parents: arrayOf(shape({
        parentId: string()
      }))
    }),
    info: shape({
      recommendationFlags: shape({
        visualNavigation: bool(),
        pipCollections: bool(),
        ACC: bool(),
        collections: bool(),
        batItems: bool(),
        packages: bool(),
        frequentlyBoughtTogether: bool(),
        bundles: bool()
      })
    }),
    identifiers: shape({
      productType: string()
    }),
    availabilityType: shape({
      discontinued: bool(),
      type: string()
    }),
    fulfillment: params({ storeId: string() }).shape({
      backordered: bool(),
      fulfillmentOptions: arrayOf(
        shape({
          type: string(),
          fulfillable: bool(),
          services: arrayOf(
            shape({
              type: string(),
              locations: arrayOf(
                shape({
                  isAnchor: bool(),
                  inventory: shape({
                    isLimitedQuantity: bool(),
                    isOutOfStock: bool()
                  })
                })
              )
            })
          )
        })
      )
    }),
    media: shape({
      image: shape({
        url: string()
      }).client()
    })
  })
};

const Service = shape({
  deliveryTimeline: string(),
  deliveryDates: shape({
    startDate: string(),
    endDate: string()
  }),
  deliveryCharge: string(),
  dynamicEta: shape({
    hours: string(),
    minutes: string()
  }),
  hasFreeShipping: bool(),
  deliveryMessage: string(),
  freeDeliveryThreshold: number({ float: true }),
  earliestDeliveryDate: string(),
  locations: arrayOf(shape({
    curbsidePickupFlag: bool(),
    isBuyInStoreCheckNearBy: bool(),
    distance: number({ float: true }),
    inventory: shape({
      isOutOfStock: bool(),
      isInStock: bool(),
      isLimitedQuantity: bool(),
      isUnavailable: bool(),
      quantity: number(),
      maxAllowedBopisQty: number(),
      minAllowedBopisQty: number()
    }),
    isAnchor: bool(),
    locationId: string(),
    state: string(),
    storeName: string(),
    storePhone: string(),
    type: string()
  })),
  shipFromFastestLocation: bool(),
  type: string(),
  totalCharge: number({ float: true }),
  optimalFulfillment: bool()
});

const Pricing = params({ storeId: string(), isBrandPricingPolicyCompliant: bool() }).shape({
  original: number({ float: true }),
  mapAboveOriginalPrice: bool(),
  conditionalPromotions: arrayOf(shape({
    promotionId: string(),
    skuItemGroup: string(),
    promotionTags: arrayOf(string()),
    subExperienceTag: string(),
    eligibilityCriteria: arrayOf(shape({
      itemGroup: string(),
      minThresholdVal: number({ float: true }),
      thresholdType: string()
    })),
    reward: shape({
      tiers: arrayOf(shape({
        minThresholdVal: number({ float: true }),
        thresholdType: string(),
        rewardVal: number({ float: true }),
        rewardType: string(),
        rewardLevel: string(),
        maxAllowedRewardAmount: number({ float: true })
      }))
    })
  })),
  mapDetail: shape({
    percentageOff: number({ float: true }),
    dollarOff: number({ float: true }),
    effectiveMap: number({ float: true }),
    mapPolicy: string(),
    mapOriginalPriceViolation: bool(),
    mapSpecialPriceViolation: bool()
  }),
  message: string(),
  promotion: shape({
    type: string(),
    description: shape({
      shortDesc: string(),
      longDesc: string()
    }),
    dollarOff: number({ float: true }),
    percentageOff: number({ float: true }),
    experienceTag: string(),
    subExperienceTag: string()
  }),
  promotionalAdjustments: arrayOf(shape({
    type: string(),
    description: shape({
      shortDesc: string(),
      longDesc: string()
    }),
    dollarOff: number({ float: true }),
    percentageOff: number({ float: true }),
    promoId: string()
  })),
  specialBuy: number({ float: true }),
  value: number({ float: true })
});

export const BundlesDataModel = {
  products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf(shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      productLabel: string(),
      brandName: string(),
      canonicalUrl: string(),
      hideItem: string()
    }),
    availabilityType: shape({
      discontinued: bool(),
      buyable: bool(),
      status: bool(),
      type: string()
    }),
    fulfillment: params({ storeId: string(), zipCode: string() }).shape({
      anchorStoreStatus: bool(),
      anchorStoreStatusType: string(),
      backordered: bool(),
      backorderedShipDate: string(),
      bossExcludedShipStates: string(),
      sthExcludedShipState: string(),
      excludedShipStates: string(),
      seasonStatusEligible: bool(),
      fulfillmentOptions: arrayOf(shape({
        type: string(),
        fulfillable: bool(),
        services: arrayOf(Service),
        priorityDeliveryOptions: arrayOf(shape({
          date: string(),
          timeline: string(),
          totalCharge: number({ float: true })
        }))
      })),
      fulfillmentBundleMessage: string(),
      onlineStoreStatus: bool(),
      onlineStoreStatusType: string()
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        sizes: arrayOf(string())
      }))
    }),
    bundleSpecificationDetails: shape({
      experienceType: string(),
      type: string(),
      brand: string(),
      components: arrayOf(shape({
        defaultProductId: string(),
        quantity: string(),
        type: string(),
        selectableVariants: arrayOf(shape({
          name: string(),
          id: string(),
          guid: string(),
          value: string()
        }))
      })),
      accessories: shape({
        categories: arrayOf(shape({
          categoryName: string(),
          offerings: arrayOf(shape({
            offeringName: string(),
            products: arrayOf(shape({
              defaultProductId: string(),
              type: string(),
              quantity: string()
            }))
          }))
        }))
      })
    }),
    pricing: Pricing,
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string(),
        totalReviews: string()
      })
    })
  }))
};

export const BundleProductsDataModel = {
  products: params({ itemIds: arrayOf(string().isRequired()).isRequired(), dataSource: string() }).arrayOf({
    itemId: string(),
    dataSource: string(),
    identifiers: shape({
      applianceType: string(),
      itemId: string(),
      productType: string(),
      brandName: string(),
      productLabel: string(),
      storeSkuNumber: string(),
      omsThdSku: string()
    }),
    pricing: Pricing,
    fulfillment: params({ storeId: string() }).shape({
      backordered: bool(),
      fulfillmentOptions: arrayOf(
        shape({
          type: string(),
          fulfillable: bool(),
          services: arrayOf(
            shape({
              type: string(),
              locations: arrayOf(
                shape({
                  isAnchor: bool(),
                  inventory: shape({
                    isLimitedQuantity: bool(),
                    isOutOfStock: bool()
                  })
                })
              )
            })
          )
        })
      )
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        sizes: arrayOf(string())
      }))
    }),
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string(),
        totalReviews: string()
      })
    })
  })
};

const ReviewProductDataStore = shape({
  FilteredReviewStatistics: shape({
    AverageOverallRating: string(),
    TotalReviewCount: number()
  })
});

export const BundleRating = {
  reviews: params({
    itemId: string().isRequired(),
    searchTerm: string(),
    sortBy: string(),
    startIndex: number(),
    filters: customType('ReviewsFilterInput')
      .shape({
        isVerifiedPurchase: bool(),
        prosCons: string(),
        starRatings: arrayOf(number())
      })
  }).shape({
    Includes: shape({
      Products: shape({
        store: ReviewProductDataStore,
        items: arrayOf(ReviewProductDataStore)
      })
    }),
    TotalResults: number()
  })
};